import { useCallback, useMemo, useState } from 'react';

import { AspectRatio, Box, Flex, Spinner } from '@vimeo/bokeh';
import { EmbedPlayer, EmbedPlayerUtility } from '@vimeo/embed-player';

import { getEventIdFromClip } from '../utils/helper';
import { Clip } from '../utils/types';
import { usePlayer } from './hooks/usePlayer';

const PLAYER_CARD_ASPECT_RATIO = 16 / 9;
const FULL_SIZE_PROPS = {
  width: '100%',
  height: '100%',
} as const;
const ABSOLUTE_FILL_PROPS = {
  ...FULL_SIZE_PROPS,
  position: 'absolute',
  inset: '0',
} as const;
const THUMBNAIL_PROPS = {
  ...FULL_SIZE_PROPS,
  objectFit: 'cover',
  userSelect: 'none',
  backgroundColor: 'black',
} as const;

interface Props {
  clip: Clip;
  name: string;
  playerEmbedUrl: string;
  isScriptLoaded: boolean;
  clipId?: string;
  embedPlayerConfigUrl?: string;
  thumbnailSrc?: string;
  thumbnailSrcSet?: string;
  playOnHover?: boolean;
}

const getHoverPreviewUrl = (url: string): string => {
  return EmbedPlayerUtility.createEmbedUrl(url, {
    autoplay: 1,
    muted: 1,
    controls: 0,
    autopause: 0,
    quality: '240p',
    loop: 1,
    background: 1,
    force_embed: 1,
    title: 0,
    byline: 0,
    portrait: 0,
  });
};

export const VideoCardPlayer = ({
  clip,
  clipId,
  embedPlayerConfigUrl,
  name,
  playerEmbedUrl,
  playOnHover = false,
  thumbnailSrc,
  thumbnailSrcSet,
  isScriptLoaded,
}: Props) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPreview, setHasStartedPreview] = useState(false);
  const { player, isInitialized, onPlayerInitialized } = usePlayer();

  const eventId = getEventIdFromClip(clip);
  const canUseHoverPreview = playOnHover && !eventId && Boolean(playerEmbedUrl);
  const hoverPreviewUrl = useMemo(() => getHoverPreviewUrl(playerEmbedUrl), [playerEmbedUrl]);

  const isHoverPreviewActive = canUseHoverPreview && isHovering;
  const isHoverPreviewMounted = canUseHoverPreview && hasStartedPreview;

  const onMouseEnter = useCallback(() => {
    setIsHovering(true);
    setHasStartedPreview(true);
    if (player) {
      setIsPlaying(false);
      void player
        .setCurrentTime(0)
        .catch(() => undefined)
        .finally(() => {
          void player.play().catch(() => undefined);
        });
    }
  }, [player]);

  const onMouseLeave = useCallback(() => {
    setIsHovering(false);
    if (player) {
      setIsPlaying(false);
      void player.pause().catch(() => undefined);
    }
  }, [player]);

  const mediaContent = useMemo(() => {
    if (canUseHoverPreview) {
      return (
        <>
          {isHoverPreviewMounted && isScriptLoaded && (
            <Box
              {...ABSOLUTE_FILL_PROPS}
              border="0"
              zIndex="0"
              opacity={isInitialized && isHoverPreviewActive ? 1 : 0}
            >
              <EmbedPlayer
                key={`${eventId}-embed`}
                title={name}
                src={hoverPreviewUrl}
                style={{ pointerEvents: 'none' }}
                onPlayerAPIReady={(playerAPI) => onPlayerInitialized(playerAPI)}
                onTimeupdate={() => setIsPlaying(true)}
              />
            </Box>
          )}
          {isHoverPreviewActive && !isPlaying && (
            <Flex
              {...ABSOLUTE_FILL_PROPS}
              alignItems="center"
              justifyContent="center"
              background="transparent"
              zIndex="2"
            >
              <Spinner size="md" />
            </Flex>
          )}
          <Box
            as="img"
            {...THUMBNAIL_PROPS}
            {...ABSOLUTE_FILL_PROPS}
            src={thumbnailSrc}
            srcSet={thumbnailSrcSet}
            alt=""
            pointerEvents="none"
            opacity={isHoverPreviewActive && isPlaying ? 0 : 1}
            zIndex="1"
          />
        </>
      );
    }
    if (eventId || !embedPlayerConfigUrl) {
      return <EmbedPlayer key={`${eventId}-embed`} src={playerEmbedUrl} disableAPI />;
    }
    return <EmbedPlayer title={name} src={playerEmbedUrl} configUrl={embedPlayerConfigUrl} />;
  }, [
    canUseHoverPreview,
    isHoverPreviewActive,
    isHoverPreviewMounted,
    isPlaying,
    thumbnailSrc,
    thumbnailSrcSet,
    hoverPreviewUrl,
    name,
    playerEmbedUrl,
    embedPlayerConfigUrl,
    eventId,
    isInitialized,
    isScriptLoaded,
    onPlayerInitialized,
  ]);

  if (!clipId) return null;

  return (
    <Box
      position="relative"
      minW="8rem"
      aspectRatio={PLAYER_CARD_ASPECT_RATIO}
      outline="0"
      borderRadius="md"
      overflow="hidden"
      backgroundColor="black"
      _focusWithin={{ '[data-component-id="decoration-wrapper"]': { opacity: 1 } }}
      data-group
      data-component-id="video-card-thumbnail"
      onMouseEnter={canUseHoverPreview ? onMouseEnter : undefined}
      onMouseLeave={canUseHoverPreview ? onMouseLeave : undefined}
    >
      <AspectRatio ratio={PLAYER_CARD_ASPECT_RATIO}>{mediaContent}</AspectRatio>
    </Box>
  );
};
